import { useCollection } from '@cloudscape-design/collection-hooks';
import Grid from '@risk-smart/themed-cloudscape-components/grid';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { defaultPropertyFilterI18nStrings } from '@risksmart-app/components/src/table/propertyFilterI18nStrings';
import type { ObligationType as Obligation_Type_Enum } from '@risksmart-app/domain/src/types/consts/obligation-type';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { drillDownGridDefinition } from 'src/blocks/drill-down-column';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import PageFilterContainer from 'src/components/page-filter-container/PageFilterContainer';
import PropertyFilterPanel from 'src/components/property-filter-panel';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';

import { useGetObligationsRegister } from '@/hooks/queries';
import { useGetLatestObligationAssessmentResults } from '@/hooks/queries/assessment-result/useGetLatestObligationAssessmentResults';
import { useGetFormCustomisationByParentTypes } from '@/hooks/queries/form-configuration/useGetFormCustomisationByParentTypes';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useAddCustomAttributes } from '@/utils/table/hooks/useAddCustomAttributes';
import { useCreateFilterOptions } from '@/utils/table/hooks/useCreateFilterOptions';
import { useCreateFilterProperties } from '@/utils/table/hooks/useCreateFilterProperties';
import { addObligationUrl } from '@/utils/urls';

import { useGetFieldConfig } from '../config';
import { useGetLabelledFields } from '../useLabelledFields';
import type { DashboardSelection } from './selection';
import { applySelection, typeOrder } from './selection';
import styles from './style.module.scss';
import Type from './Type';

const Page: FC = () => {
  const { t } = useTranslation();
  const title = t('obligations.dashboard_title');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const navigate = useNavigate();

  // Object-level toolbar (CUS-161) — flag-gated. The dashboard's only action is
  // "Add Obligation", surfaced as the toolbar's primary icon button
  // (permission-gated by the resolver). Legacy `actions` is the flag-off fallback.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title },
      actions: {
        primary: [
          {
            id: 'create',
            label: t('obligations.create_new_button'),
            iconName: 'plus',
            onClick: () => navigate(addObligationUrl()),
            permission: 'insert:obligation',
            canHaveAccessAsContributor: true,
          },
        ],
      },
    }),
    [title, t, navigate]
  );

  const [selection, setSelection] = useState<DashboardSelection>({});
  const { data, loading: loadingObligations } = useGetObligationsRegister({
    queryArgs: {},
  });
  // Error notifications are raised by the hook itself (useErrorNotification
  // inside createQueryHook), matching the legacy onError toast.
  const { data: latestAssessmentResults, loading: loadingAssessments } =
    useGetLatestObligationAssessmentResults({ queryArgs: {} });

  const { data: formCustomisation } = useGetFormCustomisationByParentTypes({
    queryArgs: { parentTypes: [Parent_Type_Enum.Obligation] },
  });

  const labelledFields = useGetLabelledFields(
    data?.obligation,
    latestAssessmentResults?.obligation_assessment_result
  );
  const fields = useGetFieldConfig();
  const { tableFields, tableData } = useAddCustomAttributes({
    fields,
    data: labelledFields,
    customAttributeSchema:
      formCustomisation?.form_configuration?.[0]?.customAttributeSchema ?? null,
    useRelativeDates: false,
  });
  const filteringProperties = useCreateFilterProperties(
    tableFields,
    formCustomisation?.form_configuration ?? null
  );

  const { items, propertyFilterProps } = useCollection(tableData, {
    propertyFiltering: {
      filteringProperties,
    },
    selection: {},
  });

  const fixedFilterOptions = useCreateFilterOptions(
    tableFields,
    tableData,
    propertyFilterProps.filteringOptions
  );

  const onSelectItem = (obligationId: string, type: Obligation_Type_Enum) => {
    setSelection((previous) => applySelection(previous, type, obligationId));
  };

  return (
    <PageLayout
      helpTranslationKey={'obligations.dashboardHelp'}
      title={title}
      pageTitle={title}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Permission
            permission={'insert:obligation'}
            canHaveAccessAsContributor={true}
          >
            <Button variant={'primary'} href={addObligationUrl()}>
              {t('obligations.create_new_button')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <PageFilterContainer>
        <PropertyFilterPanel
          {...propertyFilterProps}
          filteringOptions={fixedFilterOptions}
          i18nStrings={defaultPropertyFilterI18nStrings}
          virtualScroll={true}
        />
      </PageFilterContainer>
      <div className={styles.dashboard}>
        {/* Reflow rather than scroll — the breakpoints and why they sit where they
            do are documented on `drillDownGridDefinition`. */}
        <Grid gridDefinition={drillDownGridDefinition(typeOrder.length)}>
          {typeOrder.map((type, depth) => (
            <Type
              key={type}
              loading={loadingObligations || loadingAssessments}
              obligations={items}
              type={type}
              selectedId={
                depth === 0 ? undefined : selection[typeOrder[depth - 1]]
              }
              selectedItemId={selection[type]}
              onSelectAction={onSelectItem}
            />
          ))}
        </Grid>
      </div>
    </PageLayout>
  );
};

export default Page;
